import { useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import 'assets/css/MiniCalendar.css';
import { Flex, Text, Icon, useStyleConfig } from '@chakra-ui/react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';

type Value = Date | null | [Date | null, Date | null];

interface MiniCalendarProps {
  selectRange?: boolean;
  onChange?: (value: Value) => void;
  value?: Value;
  /** Tamaño del calendario: "sm" | "md" | "lg" (default: "md") */
  size?: "sm" | "md" | "lg";
  /** Permite click en header para subir a vista año/década */
  drillable?: boolean;
  /** Custom tile content — receives { date, view } from react-calendar */
  tileContent?: ({ date, view }: { date: Date; view: string }) => React.ReactNode;
  [x: string]: any;
}

// zoom afecta layout (a diferencia de transform:scale) — escala tiles, nav y texto
const zoomMap: Record<string, number> = { sm: 0.75, md: 1, lg: 1.2 };

export default function MiniCalendar({
  selectRange = false,
  onChange,
  value: controlledValue,
  size = "md",
  drillable = false,
  tileContent,
  ...rest
}: MiniCalendarProps) {
  const styles = useStyleConfig('MiniCalendar', { size, ...rest });
  const [internalValue, setInternalValue] = useState<Value>(new Date());

  const value = controlledValue !== undefined ? controlledValue : internalValue;

  const handleChange = (val: Value, _event: React.MouseEvent<HTMLButtonElement>) => {
    setInternalValue(val);
    onChange?.(val);
  };

  const zoom = zoomMap[size] ?? 1;

  return (
    <Flex
      __css={styles}
      sx={{ '& .react-calendar': { zoom } }}
      className={drillable ? 'mini-cal--drillable' : undefined}
      {...rest}
    >
      <Calendar
        onChange={handleChange}
        value={value}
        selectRange={selectRange}
        {...(drillable ? { defaultView: 'month' } : { view: 'month' })}
        tileContent={tileContent ?? ((() => <Text color="brand.500" />) as any)}
        prevLabel={<Icon as={MdChevronLeft} w="24px" h="24px" mt="4px" />}
        nextLabel={<Icon as={MdChevronRight} w="24px" h="24px" mt="4px" />}
      />
    </Flex>
  );
}
